import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Users,
  Gauge,
  FileText,
  CreditCard,
  BarChart3,
  Settings,
  Menu,
  X,
  Droplets,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

interface SidebarItem {
  id: string;
  label: string;
  path: string;
  icon: React.ReactNode;
  badge?: string | number;
  description?: string;
}

interface SidebarSection {
  title: string;
  items: SidebarItem[];
}

interface SidebarProps {
  is_collapsed?: boolean;
  on_collapse_toggle?: (collapsed: boolean) => void;
}

const STORAGE_KEY = 'aquapura_sidebar_collapsed';

// Helper to safely get from localStorage
const getStoredCollapsedState = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const item = window.localStorage.getItem(STORAGE_KEY);
    return item === 'true';
  } catch (error) {
    console.warn('Failed to read sidebar state from localStorage:', error);
    return false;
  }
};

// Helper to safely set to localStorage
const setStoredCollapsedState = (value: boolean): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, String(value));
  } catch (error) {
    console.warn('Failed to save sidebar state to localStorage:', error);
  }
};

const sidebarSections: SidebarSection[] = [
  {
    title: 'Main Menu',
    items: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        path: '/',
        icon: <LayoutDashboard size={20} />,
        // description: 'Overview and analytics'
      },
      {
        id: 'clients',
        label: 'Clients',
        path: '/clients',
        icon: <Users size={20} />,
        // description: 'Manage customer accounts'
      },
      {
        id: 'meters',
        label: 'Meters',
        path: '/meters',
        icon: <Gauge size={20} />,
        // description: 'Monitor water meters'
      },
      {
        id: 'readings',
        label: 'Readings',
        path: '/readings',
        icon: <Droplets size={20} />,
        // description: 'View consumption data'
      }
    ]
  },
  {
    title: 'Finance',
    items: [
      {
        id: 'invoices',
        label: 'Invoices',
        path: '/invoices',
        icon: <FileText size={20} />,
        // description: 'Billing and invoices'
      },
      {
        id: 'payments',
        label: 'Payments',
        path: '/payments',
        icon: <CreditCard size={20} />,
        // description: 'Payment transactions'
      },
      {
        id: 'reports',
        label: 'Reports',
        path: '/reports',
        icon: <BarChart3 size={20} />,
        // description: 'Financial reports'
      }
    ]
  },
  {
    title: 'System',
    items: [
      {
        id: 'settings',
        label: 'Settings',
        path: '/settings',
        icon: <Settings size={20} />,
        // description: 'System configuration'
      }
    ]
  }
];

export const Sidebar: React.FC<SidebarProps> = ({ 
  is_collapsed, 
  on_collapse_toggle 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isClient, setIsClient] = useState(false);

  // Initialize collapsed state - controlled vs uncontrolled
  const [internalCollapsed, setInternalCollapsed] = useState(() => {
    // Don't access localStorage during SSR
    if (typeof window === 'undefined') return false;
    return getStoredCollapsedState();
  });

  // Determine if component is controlled
  const isControlled = is_collapsed !== undefined;
  const collapsed = isControlled ? is_collapsed : internalCollapsed;

  // Set client-side flag after mount
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Save to localStorage when state changes (only for uncontrolled)
  useEffect(() => {
    if (!isControlled && isClient) {
      setStoredCollapsedState(internalCollapsed);
    }
  }, [internalCollapsed, isControlled, isClient]);

  // Determine active item based on current route
  const getActiveItem = useCallback(() => {
    const currentPath = location.pathname;
    for (const section of sidebarSections) {
      const item = section.items.find(item => item.path === currentPath);
      if (item) return item.id;
    }
    return 'dashboard';
  }, [location.pathname]);

  const activeItem = getActiveItem();

  // Handle responsive behavior
  useEffect(() => {
    const checkMobile = () => {
      const isMobileView = window.innerWidth < 768;
      setIsMobile(isMobileView);
      if (!isMobileView) {
        setMobileOpen(false);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileOpen]);

  const handleToggle = useCallback(() => {
    if (isMobile) {
      setMobileOpen(prev => !prev);
    } else {
      const newState = !collapsed;
      if (isControlled) {
        on_collapse_toggle?.(newState);
      } else {
        setInternalCollapsed(newState);
      }
    }
  }, [isMobile, collapsed, isControlled, on_collapse_toggle]);

  const handleItemClick = useCallback((item: SidebarItem) => {
    navigate(item.path);
    if (isMobile) {
      setMobileOpen(false);
    }
  }, [navigate, isMobile]);

  const renderNavItem = (item: SidebarItem, isCollapsed: boolean) => {
    const isActive = activeItem === item.id;
    
    return (
      <li key={item.id}>
        <button
          onClick={() => handleItemClick(item)}
          className={`w-full flex items-center transition-all duration-200 group relative ${
            isCollapsed ? 'justify-center px-3 py-3' : 'gap-3 px-3 py-2.5'
          } rounded-lg ${
            isActive 
              ? 'bg-forest-green/10 text-forest-green border border-forest-green/20' 
              : 'text-medium-gray hover:bg-off-white hover:text-charcoal'
          }`}
          aria-label={item.label}
          aria-current={isActive ? 'page' : undefined}
        >
          {/* Active indicator bar */}
          {isActive && (
            <span className="absolute left-0 top-0 w-1 h-full bg-forest-green rounded-full" />
          )}
          
          <span className={`flex-shrink-0 transition-transform duration-200 ${isActive ? 'text-forest-green' : 'group-hover:scale-110'}`}>
            {item.icon}
          </span>
          
          {!isCollapsed && (
            <div className="flex-1 text-left min-w-0 flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-medium truncate ${isActive ? 'font-semibold' : ''}`}>
                  {item.label}
                </div>
                {item.description && !isMobile && !isActive && (
                  <div className="text-xs opacity-60 truncate">
                    {item.description}
                  </div>
                )}
              </div>
              
              {/* Badge */}
              {item.badge && (
                <span
                  className={`px-2 py-0.5 text-xs font-semibold rounded-full flex-shrink-0 ${
                    isActive ? 'bg-forest-green text-white' : 'bg-light-gray text-charcoal'
                  }`}
                >
                  {item.badge}
                </span>
              )}
            </div>
          )}
          
          {/* Tooltip for collapsed state */}
          {isCollapsed && !isMobile && (
            <div className="absolute left-full ml-6 px-3 py-2 bg-charcoal text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50 shadow-xl">
              <div className="font-medium">{item.label}</div>
              {item.description && (
                <div className="text-xs opacity-80 mt-0.5">{item.description}</div>
              )}
              {/* Tooltip arrow */}
              <div className="absolute right-full top-1/2 -translate-y-1/2 border-8 border-transparent border-r-charcoal" />
            </div>
          )}
          
          {/* Badge dot for collapsed state */}
          {item.badge && isCollapsed && (
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-forest-green rounded-full" />
          )}
        </button>
      </li>
    );
  };

  const renderSection = (section: SidebarSection, isCollapsed: boolean) => (
    <div key={section.title} className="mb-6 last:mb-0">
      {!isCollapsed ? (
        <div className="px-3 mb-2">
          <h2 className="text-xs font-semibold text-medium-gray uppercase tracking-wider">
            {section.title}
          </h2>
        </div>
      ) : (
        <div className="px-3 mb-2">
          <div className="h-px bg-light-gray" />
        </div>
      )}
      <ul className="space-y-1">
        {section.items.map(item => renderNavItem(item, isCollapsed))}
      </ul>
    </div>
  );

  // Mobile overlay
  if (isMobile) {
    return (
      <>
        {/* Mobile Menu Button */}
        <button
          onClick={handleToggle}
          className="fixed top-4 left-4 z-50 p-3 bg-gradient-to-br from-forest-green to-dark-forest text-white rounded-xl shadow-xl hover:shadow-2xl transition-all duration-200 md:hidden active:scale-95"
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {/* Backdrop */}
        {mobileOpen && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Mobile Sidebar */}
        <aside
          className={`fixed top-0 left-0 w-80 max-w-[85vw] bg-white h-screen z-50 flex flex-col shadow-2xl md:hidden transition-transform duration-300 ease-out ${
            mobileOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
          role="dialog"
          aria-label="Navigation menu"
        >
          {/* Header */}
          <div className="p-6 border-b border-light-gray bg-gradient-to-r from-off-white to-white">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-gradient-to-br from-forest-green to-dark-forest rounded-xl flex items-center justify-center shadow-md">
                <Droplets size={22} className="text-lime-green" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-charcoal tracking-tight">
                  Aqua Pura
                </h1>
                <p className="text-xs text-medium-gray font-medium">
                  Powered by BinByte
                </p>
              </div>
            </div>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto px-3 py-4 scrollbar-thin scrollbar-thumb-light-gray scrollbar-track-transparent">
            {sidebarSections.map(section => renderSection(section, false))}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-light-gray bg-gradient-to-r from-off-white to-white">
            <div className="text-xs text-center text-medium-gray">
              <span className="font-medium">v2.0.0</span>
              <span className="mx-2">•</span>
              <span>© {new Date().getFullYear()} BinByte</span>
            </div>
          </div>
        </aside>
      </>
    );
  }

  // Desktop Sidebar
  return (
    <aside
      style={{ width: collapsed ? '80px' : '280px' }}
      className="bg-white border-r border-light-gray h-screen sticky top-0 flex flex-col  md:flex shadow-sm transition-all duration-300 ease-in-out"
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Header */}
      <div className="p-6 border-b border-light-gray bg-gradient-to-r from-off-white to-white min-h-[88px] flex items-center">
        {!collapsed ? (
          <div className="flex items-center gap-3 w-full">
            <div className="w-10 h-10 bg-gradient-to-br from-forest-green to-dark-forest rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
              <Droplets size={22} className="text-lime-green" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-lg font-bold text-charcoal tracking-tight truncate">
                Aqua Pura
              </h1>
              <p className="text-xs text-medium-gray font-medium truncate">
                Powered by BinByte
              </p>
            </div>
          </div>
        ) : (
          <div className="flex justify-center w-full">
            <div className="w-10 h-10 bg-gradient-to-br from-forest-green to-dark-forest rounded-xl flex items-center justify-center shadow-md">
              <Droplets size={22} className="text-lime-green" />
            </div>
          </div>
        )}
      </div>
      
      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 scrollbar-thin scrollbar-thumb-light-gray scrollbar-track-transparent">
        {sidebarSections.map(section => renderSection(section, collapsed))}
      </nav>
      
      {/* Footer - Collapse Toggle */}
      <div className="p-4 border-t border-light-gray bg-gradient-to-r from-off-white to-white">
        <button
          onClick={handleToggle}
          className={`w-full flex items-center rounded-lg font-medium transition-all duration-200 active:scale-98 ${
            collapsed 
              ? 'bg-gradient-to-r from-forest-green to-dark-forest text-white justify-center shadow-md hover:shadow-lg px-3 py-3' 
              : 'bg-gradient-to-r from-off-white to-light-gray text-charcoal hover:from-forest-green hover:to-dark-forest hover:text-white shadow-sm hover:shadow-md px-4 py-3 gap-3'
          }`}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronRight size={20} />
          ) : (
            <>
              <ChevronLeft size={20} className="flex-shrink-0" />
              <span className="text-sm whitespace-nowrap">
                Collapse Menu
              </span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
};